// Color schemes — the ITZKATU visual identity is intentionally tight: only
// three cohesive palettes, each a strict 3-color hierarchy the media renderers
// use to produce a duotone/tritone (shadow → accent → highlight).
//
//   light   → white BG · purple accent · black shadow (default)
//   mustard → yellow BG · purple accent · black shadow
//   night   → black BG · purple accent · off-white highlight
//
// Every effect (pixel, dither, ASCII, CRT, pixel-lines) reads these via the
// CSS custom properties injected by Layout / MediaTile so a scheme change in
// admin re-tints EVERY visual instantly.

export const SCHEMES = {
    light: {
        bg: "bg-[#FFFFFF]",
        text: "text-[#0E0E0E]",
        subtext: "text-[#3A3A3A]",
        kicker: "text-[#5D3FD3]",
        accent: "text-[#5D3FD3]",
        dotColor: "text-[#5D3FD3]",
        cardBg: "bg-[#F5F5F5]",
        cardText: "text-[#0E0E0E]",
        border: "border-[#EAEAEA]",
    },
    mustard: {
        bg: "bg-[#F5D547]",
        text: "text-[#0E0E0E]",
        subtext: "text-[#3A2A00]",
        kicker: "text-[#5D3FD3]",
        accent: "text-[#5D3FD3]",
        dotColor: "text-[#5D3FD3]",
        cardBg: "bg-[#FFE87A]",
        cardText: "text-[#0E0E0E]",
        border: "border-[#D9B520]",
    },
    // Warm light-grey base + magenta accent — quiet editorial mood, close to white.
    smoke: {
        bg: "bg-[#EDEAE5]",
        text: "text-[#141212]",
        subtext: "text-[#3A3835]",
        kicker: "text-[#D9376E]",
        accent: "text-[#D9376E]",
        dotColor: "text-[#D9376E]",
        cardBg: "bg-[#F5F3EF]",
        cardText: "text-[#141212]",
        border: "border-[#CFCBC6]",
    },
    // Cool light-grey base + orange accent — complementary counterpart to smoke.
    stone: {
        bg: "bg-[#E5E7E9]",
        text: "text-[#0F1214]",
        subtext: "text-[#2E3438]",
        kicker: "text-[#E85E2C]",
        accent: "text-[#E85E2C]",
        dotColor: "text-[#E85E2C]",
        cardBg: "bg-[#EFF1F3]",
        cardText: "text-[#0F1214]",
        border: "border-[#C5CACD]",
    },
    night: {
        bg: "bg-[#0E0E0E]",
        text: "text-[#F5F1E8]",
        subtext: "text-[#D8D3C6]",
        kicker: "text-[#7C5CFF]",
        accent: "text-[#7C5CFF]",
        dotColor: "text-[#7C5CFF]",
        cardBg: "bg-[#1A1A1A]",
        cardText: "text-[#F5F1E8]",
        border: "border-[#3A3A3A]",
    },
    // Charcoal — softer dark. Sits just above pure black so it doesn't
    // clash with mustard / night when placed next to them in the same scroll.
    charcoal: {
        bg: "bg-[#1F1F22]",
        text: "text-[#EDEAE0]",
        subtext: "text-[#B8B4A8]",
        kicker: "text-[#F5D547]",
        accent: "text-[#F5D547]",
        dotColor: "text-[#F5D547]",
        cardBg: "bg-[#2A2A2E]",
        cardText: "text-[#EDEAE0]",
        border: "border-[#3E3E44]",
    },
};

export const SCHEME_KEYS = Object.keys(SCHEMES);

// Raw hex palettes — the single source of truth. Each scheme = 3 clear tones.
export const SCHEMES_HEX = {
    light:   { bg: "#FFFFFF", text: "#0E0E0E", subtext: "#3A3A3A", accent: "#5D3FD3", accent2: "#F5D547", card: "#F5F5F5", border: "#EAEAEA" },
    mustard: { bg: "#F5D547", text: "#0E0E0E", subtext: "#3A2A00", accent: "#5D3FD3", accent2: "#FFFFFF", card: "#FFE87A", border: "#D9B520" },
    // Warm light-grey + magenta.
    smoke:   { bg: "#EDEAE5", text: "#141212", subtext: "#3A3835", accent: "#D9376E", accent2: "#141212", card: "#F5F3EF", border: "#CFCBC6" },
    // Cool light-grey + orange. Complementary to smoke on the color wheel.
    stone:   { bg: "#E5E7E9", text: "#0F1214", subtext: "#2E3438", accent: "#E85E2C", accent2: "#0F1214", card: "#EFF1F3", border: "#C5CACD" },
    // Night — black/PURPLE (brand identity). Electric purple pulled slightly
    // lighter than #5D3FD3 so small text stays readable on black. Yellow moved
    // to accent2 (After Dark keeps its own gold identity separately).
    night:    { bg: "#0E0E0E", text: "#F5F1E8", subtext: "#D8D3C6", accent: "#7C5CFF", accent2: "#F5D547", card: "#1A1A1A", border: "#3A3A3A" },
    // Charcoal — softer dark. Same accent language as night but pulled up
    // toward #1F1F22 so it never clashes when adjacent to night or mustard.
    charcoal: { bg: "#1F1F22", text: "#EDEAE0", subtext: "#B8B4A8", accent: "#F5D547", accent2: "#5D3FD3", card: "#2A2A2E", border: "#3E3E44" },
};

// Legacy scheme names still stored in the DB from earlier iterations. Map them
// onto the new three so old content keeps rendering without an admin migration.
const LEGACY_MAP = {
    cream:    "light",
    white:    "light",
    gray:     "light",
    sand:     "mustard",
    mint:     "light",
    charcoal: "night",
    purple:   "mustard",
    black:    "night",
    default:  "light",
    dark:     "night",
};
function resolve(name) {
    if (!name) return "light";
    if (SCHEMES[name]) return name;
    return LEGACY_MAP[name] || "light";
}

export function schemeHex(name) {
    return SCHEMES_HEX[resolve(name)];
}

export function schemeAccentHex(name) {
    return schemeHex(name).accent;
}

// Fetch a scheme (Tailwind classes) by key with legacy fallback.
export function scheme(name) {
    return SCHEMES[resolve(name)];
}

// Read the scheme for a given home section key from site_content.
// Overload: `sectionScheme(name)` (single string) resolves that scheme name.
export function sectionScheme(siteContentOrName, sectionKey, fallback = "light") {
    if (typeof siteContentOrName === "string" && sectionKey === undefined) {
        return scheme(siteContentOrName || fallback);
    }
    const raw = (siteContentOrName || {})[`section_scheme_${sectionKey}`];
    return scheme(raw || fallback);
}
